import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBlogPost } from '../hooks/useBlogPost';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import Breadcrumb from '../components/Breadcrumb';
import RelatedPlants from '../components/blog/RelatedPlants';
import CommentList from '../components/blog/CommentList';
import CommentForm from '../components/blog/CommentForm';
import { useBlogComments } from '../hooks/useBlogComments';
import { Calendar, ArrowLeft, Loader2 } from 'lucide-react';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading, error } = useBlogPost(slug);
  const { 
    comments, 
    loading: commentsLoading, 
    error: commentsError, 
    submitting, 
    addComment, 
    deleteComment 
  } = useBlogComments(post?.id);
  const { setItems } = useBreadcrumb();

  React.useEffect(() => {
    if (post) {
      setItems([
        { label: 'Home', path: '/' },
        { label: 'Blog', path: '/blog' },
        { label: post.title, path: `/blog/${post.slug}` }
      ]);
    }
  }, [post, setItems]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-red-600">
          {error || 'Blog post not found'}
        </p>
      </div>
    );
  }

  const publishDate = new Date(post.published_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-[50vh] min-h-[400px] bg-gray-900">
        <img 
          src={post.image_url} 
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 text-white/80 mb-4">
                <span className="text-primary-400">{post.category}</span>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{publishDate}</span>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {post.title}
              </h1>
              <p className="text-xl text-gray-200">
                {post.excerpt}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <Breadcrumb />
        </div>

        <div className="max-w-4xl mx-auto">
          <article className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </article>

          {post.plants && post.plants.length > 0 && (
            <div className="mb-8">
              <RelatedPlants plants={post.plants} />
            </div>
          )}

          <div className="mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <CommentForm 
            onSubmitComment={addComment}
            submitting={submitting}
          />
          
          <div className="mt-8">
            {commentsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
              </div>
            ) : commentsError ? (
              <div className="text-red-600 bg-red-50 p-4 rounded-lg">
                Error loading comments: {commentsError}
              </div>
            ) : (
              <CommentList 
                comments={comments}
                onDeleteComment={deleteComment}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}